import { addDays, startOfWeek } from "./calendar-utils";

/**
 * Weeks of context rendered either side of the anchor week. Fixed on purpose:
 * the day run — and therefore the fetch — must not move when you zoom.
 */
export const WEEK_PAD_WEEKS = 1;

function normalizeFactor(widthZoomPercent) {
  const factor = Number(widthZoomPercent) / 100;
  return Number.isFinite(factor) && factor > 0 ? factor : 1;
}

/**
 * Walk `count` days away from `from` in `step` direction, skipping Sundays when
 * they are hidden. Returned in calendar order (earliest first).
 */
function padDays(from, count, step, hideSunday) {
  const days = [];
  let cursor = from;
  let guard = 0;
  while (days.length < count && guard < count * 2 + 14) {
    guard += 1;
    cursor = addDays(cursor, step);
    if (hideSunday && cursor.weekday === 7) continue;
    days.push(cursor);
  }
  return step < 0 ? days.reverse() : days;
}

/**
 * The run of days the week view renders: the anchor week plus a FIXED
 * WEEK_PAD_WEEKS either side. Deliberately independent of the zoom — the day set
 * (and therefore the fetch) stays put while you zoom, so zooming is a pure width
 * change with no refetch, no remount and no loading flash. Below 100% the extra
 * days fill what would otherwise be blank space; above it they are scrollable
 * context.
 *
 * Returns:
 *   days             — every rendered day, in calendar order
 *   anchorStartIndex — index of the anchor week's first day in `days`
 *   anchorLength     — days in the anchor week (6 when Sunday is hidden)
 *   totalColumns     — days.length, i.e. one grid track each
 */
export function getWeekDayWindow(anchorDate, { hideSunday = false } = {}) {
  const anchorLength = hideSunday ? 6 : 7;
  const weekStart = startOfWeek(anchorDate);
  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)).filter(
    (day) => !hideSunday || day.weekday !== 7,
  );
  const pad = anchorLength * WEEK_PAD_WEEKS;
  const days = [
    ...padDays(weekDays[0], pad, -1, hideSunday),
    ...weekDays,
    ...padDays(weekDays[weekDays.length - 1], pad, 1, hideSunday),
  ];
  return { days, anchorStartIndex: pad, anchorLength, totalColumns: days.length };
}

/**
 * How the fixed day run lays out at the current zoom. All of it is derived, so
 * a zoom change re-renders styles only — the columns themselves never move.
 *
 * At factor f a column is f × (pane / anchorLength), so `anchorLength / f`
 * columns fit across the pane. That ratio is exact; nothing is measured.
 */
export function weekColumnMetrics(window, widthZoomPercent) {
  const anchorLength = window?.anchorLength || 7;
  const totalColumns = window?.totalColumns || anchorLength;
  const anchorStartIndex = window?.anchorStartIndex || 0;
  const factor = normalizeFactor(widthZoomPercent);
  const columnsPerPane = anchorLength / factor;
  // Where the anchor week sits in the viewport: centred in the slack when the
  // pane shows more than a week, flush left when it shows a week or less (so the
  // default 100% view still opens on exactly Sun–Sat, as it always has).
  const slack = Math.max(columnsPerPane - anchorLength, 0);
  const columnsLeftOfWeek = Math.min(Math.round(slack / 2), anchorStartIndex);
  return {
    columnsPerPane,
    // Pane-widths of columns the grid holds. Not a content width: the gutters
    // below don't scale, so the two differ (see weekGridWidthStyle).
    columnScale: totalColumns / columnsPerPane,
    initialScrollColumns: anchorStartIndex - columnsLeftOfWeek,
  };
}

export const WEEK_GUTTER_REM = 3.5 + 2;

/**
 * `style` for the week grid's content root: wide enough to hold every rendered
 * column at the target column width. Null when the run fits its pane exactly.
 */
export function weekGridWidthStyle(metrics, gutterPx) {
  const scale = metrics?.columnScale;
  if (!(scale > 1)) return null;
  return { minWidth: `calc(${gutterPx}px + (100% - ${gutterPx}px) * ${scale})` };
}

/**
 * scrollLeft that puts the anchor week where it belongs: flush against the
 * sticky hour gutter when the pane shows a week or less, centred in the slack
 * when it shows more.
 */
export function weekAnchorScrollLeft(window, metrics, gridWidthPx, gutterPx) {
  const columns = metrics?.initialScrollColumns;
  if (!columns || !window?.totalColumns) return 0;
  return Math.max(columns * ((gridWidthPx - gutterPx) / window.totalColumns), 0);
}
